import { useState } from 'react'
import { Alert, Button, Card, Modal, Tag } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import { ADVICEPREP_PLATFORM_NAME, type BillingSubscription } from '@/api/billing'
import {
  ADVICEPREP_DEFAULT_CONNECTIONS,
  ADVICEPREP_ENTRY_CONNECTIONS,
  ADVICEPREP_TRIAL_CONNECTIONS,
  advicePrepTrialDaysLeft,
  formatAdvicePrepCurrency,
} from '../pricing'
import {
  useAdvicePrepMeteredConnections,
  useAdvicePrepOutstandingInvoice,
  useAdvicePrepTrialLimit,
  usePayAdvicePrepInvoiceMutation,
  useResumeAdvicePrepMutation,
  type AdvicePrepConnectionBilling,
} from '../hooks'
import { advicePrepTrialLimitHint } from '../trialLimit'
import AdvicePrepCurrentPeriod from './AdvicePrepCurrentPeriod'
import ConnectionsEstimator from './ConnectionsEstimator'
import Tile from './Tile'

/**
 * "Not included" rather than "Exempt": the flag does not say why a connection
 * is off the meter, and claiming an exemption it cannot verify is how a support
 * ticket starts.
 */
const CONNECTION_TAG: Record<AdvicePrepConnectionBilling, { label: string; color?: string }> = {
  billed: { label: 'Free in trial', color: 'green' },
  not_included: { label: 'Not included' },
  unknown: { label: 'Unknown' },
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString()
}

type AdvicePrepManageCardProps = {
  clientId: string
  subscription: BillingSubscription
  cardLabel?: string | null
  onUpdatePaymentMethod: () => void
  /** Ends a promo-code trial today. Resolves once the outcome has been reported. */
  onSubscribeNow?: () => Promise<void>
  subscribingNow?: boolean
  disabled?: boolean
}

export default function AdvicePrepManageCard({
  clientId,
  subscription,
  cardLabel,
  onUpdatePaymentMethod,
  onSubscribeNow,
  subscribingNow,
  disabled,
}: AdvicePrepManageCardProps) {
  const { addToast } = useToast()
  const resume = useResumeAdvicePrepMutation(clientId)
  const [connections, setConnections] = useState(ADVICEPREP_DEFAULT_CONNECTIONS)
  const [confirmSubscribeNow, setConfirmSubscribeNow] = useState(false)
  const pendingCancellation = subscription.status === 'canceled' && !subscription.isExpired
  const isPastDue = subscription.status === 'past_due'
  const isTrialing = subscription.status === 'trialing'
  const isActive = subscription.status === 'active'
  const advicePrepPrice = subscription.price.find((item) => item.platformName === ADVICEPREP_PLATFORM_NAME)
  const flatFee = advicePrepPrice?.amount ?? 0
  // A trial's coupon zeroes `amount`, so anything describing life after the
  // trial has to quote the list price instead.
  const flatFeeAfterTrial = advicePrepPrice?.fullAmount ?? 0
  const trial = useAdvicePrepTrialLimit(clientId, { subscription })
  const trialDaysLeft = advicePrepTrialDaysLeft(subscription.trialEnd ?? null)
  // Only a promo-code trial carries a discount; a standard trial runs its course.
  const canSubscribeNow = isTrialing && !!subscription.hasDiscount && !!onSubscribeNow
  const showCurrentPeriod = !isTrialing && !!subscription.upcomingInvoice
  const { metered: connectionsOnMeter } = useAdvicePrepMeteredConnections(clientId, showCurrentPeriod)

  const { data: invoice } = useAdvicePrepOutstandingInvoice(clientId, isPastDue)
  const payInvoice = usePayAdvicePrepInvoiceMutation(clientId)
  const hasOutstandingInvoice = !!invoice?.outstanding
  const outstandingAmount = invoice?.outstanding ? invoice.amountDue : null

  const handleResume = async () => {
    try {
      const result = await resume.mutateAsync()
      if (result.isSuccessful) addToast('AdvicePrep subscription resumed', 'success')
      else addToast(result.error, 'error')
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Unable to resume the subscription', 'error')
    }
  }

  const handleConfirmSubscribeNow = async () => {
    if (!onSubscribeNow) return
    await onSubscribeNow()
    setConfirmSubscribeNow(false)
  }

  const handlePayInvoice = async () => {
    try {
      const result = await payInvoice.mutateAsync()
      if (result.isSuccessful) addToast('Payment received. Access restored.', 'success')
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Unable to take the payment', 'error')
    }
  }

  return (
    <Card className="rounded-xl border border-brand-100 bg-gradient-to-br from-brand-50 to-white">
      {/* brand-700 rather than brand-500 for a trial: it has to read as a
          different state from Active plan without leaving the palette. */}
      <Tag
        className={`mb-2 rounded-full border-none px-3 py-0.5 text-xs font-semibold text-white ${
          isTrialing ? 'bg-brand-700' : 'bg-brand-500'
        }`}
      >
        {isTrialing ? 'Free trial' : pendingCancellation ? 'Ending soon' : 'Active plan'}
      </Tag>
      <div className="font-display text-lg font-bold text-brand-700">AdvicePrep</div>
      <p className="mt-1 text-sm text-gray-600">
        {isTrialing ? (
          <>
            Free until {formatDate(subscription.trialEnd)}, including{' '}
            {ADVICEPREP_TRIAL_CONNECTIONS} client Xero connections · then{' '}
            {formatAdvicePrepCurrency(flatFeeAfterTrial)} a month covering your first{' '}
            {ADVICEPREP_ENTRY_CONNECTIONS}
          </>
        ) : (
          <>
            {formatAdvicePrepCurrency(flatFee)} a month covering your first {ADVICEPREP_ENTRY_CONNECTIONS} client
            Xero connections
            {pendingCancellation ? '' : ` · next billing date ${formatDate(subscription.nextBillingDate)}`}
          </>
        )}
      </p>

      {pendingCancellation && (
        <Alert
          type="warning"
          showIcon
          className="mt-4"
          message={`Subscription cancelled. Service ends ${formatDate(subscription.currentPeriodEnd)}.`}
          description="Resume before that date to keep AdvicePrep without re-subscribing."
          action={
            <Button size="small" loading={resume.isPending} disabled={disabled} onClick={() => void handleResume()}>
              Resume
            </Button>
          }
        />
      )}

      {isPastDue && (
        <Alert
          type="error"
          showIcon
          className="mt-4"
          message="Last payment failed"
          description={
            <>
              <p className="mb-2">
                {outstandingAmount !== null
                  ? `Settling the outstanding ${formatAdvicePrepCurrency(outstandingAmount, 2)} restores access straight away. This is the whole invoice, so it includes any usage on top of the flat fee.`
                  : 'Settling the outstanding invoice restores access straight away.'}
              </p>
              {payInvoice.data?.isSuccessful === false && (
                <Alert type="error" showIcon className="mb-2" message={payInvoice.data.error} />
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="primary"
                  danger
                  loading={payInvoice.isPending}
                  disabled={disabled || !hasOutstandingInvoice}
                  onClick={() => void handlePayInvoice()}
                >
                  {outstandingAmount !== null
                    ? `Pay ${formatAdvicePrepCurrency(outstandingAmount, 2)} now`
                    : 'Pay outstanding invoice'}
                </Button>
                <Button disabled={disabled} onClick={onUpdatePaymentMethod}>
                  Update card first
                </Button>
              </div>
            </>
          }
        />
      )}

      {showCurrentPeriod && subscription.upcomingInvoice && (
        <AdvicePrepCurrentPeriod
          upcoming={subscription.upcomingInvoice}
          latest={subscription.latestInvoice}
          cardLabel={cardLabel}
          connectionsOnMeter={connectionsOnMeter}
        />
      )}

      {isTrialing && (
        <div className="mt-5 rounded-xl border border-brand-200 bg-white/70 p-4">
          <div className="font-display text-sm font-semibold text-brand-700">Your free trial</div>
          <div className="mb-3 flex flex-wrap items-start gap-3">
            <div className="grow text-xs text-gray-600">
              Nothing is charged until the trial ends, when billing starts automatically.
            </div>
            {canSubscribeNow && (
              <Button
                type="primary"
                size="small"
                loading={subscribingNow}
                disabled={disabled}
                onClick={() => setConfirmSubscribeNow(true)}
              >
                Subscribe now
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Tile
              label="Days left"
              value={trialDaysLeft === null ? '—' : String(trialDaysLeft)}
              caption={
                trialDaysLeft === 0 ? 'last day' : `ends ${formatDate(subscription.trialEnd)}`
              }
              accent="#107569"
            />
            <Tile label="Due today" value={formatAdvicePrepCurrency(0, 2)} caption="for AdvicePrep" />
            <Tile
              label="First charge"
              value={formatAdvicePrepCurrency(flatFeeAfterTrial, 2)}
              caption={`on ${formatDate(subscription.trialEnd)}`}
            />
          </div>

          {/* Withheld rather than guessed: a count missing the billing flag would
              read as a confident low number and invite "why am I blocked". */}
          {trial.countKnown && trial.used !== undefined && (
            <div className="mt-4 border-t border-brand-100 pt-4">
              <div className="flex items-baseline gap-3">
                <div className="text-sm font-medium text-gray-800">Free platform connections</div>
                <div className="grow" />
                <div className="text-sm font-semibold text-brand-700">
                  {trial.used} of {trial.limit} used
                </div>
              </div>

              <div className="mt-2 flex gap-1.5" aria-hidden>
                {Array.from({ length: trial.limit }, (_, index) => (
                  <div
                    key={index}
                    className={`h-1.5 grow rounded-full ${
                      trial.used !== undefined && index < trial.used ? 'bg-brand-500' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>

              {trial.connections.length > 0 && (
                <ul className="mt-3 list-none space-y-1.5 p-0">
                  {trial.connections.map((connection) => (
                    <li
                      key={connection.id}
                      className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2"
                    >
                      <span className="grow text-[13px] text-gray-700">
                        {connection.name} — {connection.type}
                      </span>
                      <Tag className="m-0" color={CONNECTION_TAG[connection.billing].color}>
                        {CONNECTION_TAG[connection.billing].label}
                      </Tag>
                    </li>
                  ))}
                </ul>
              )}

              {/* The allowance being spent is the fact; being blocked by it is
                  not, for a DI admin. Only they get the bare statement. */}
              <p className="mb-0 mt-3 text-xs leading-5 text-gray-500">
                {!trial.allowanceSpent
                  ? `Only Xero organisations on the AdvicePrep meter use up your free ${trial.limit}.`
                  : trial.atLimit
                    ? `${advicePrepTrialLimitHint} Adding turns back on when the trial ends on ${formatDate(subscription.trialEnd)} and billing starts.`
                    : advicePrepTrialLimitHint}
              </p>
            </div>
          )}
        </div>
      )}

      {!isActive && (
        <div className="mt-5 rounded-xl border border-brand-200 bg-white/70 p-4">
          <div className="font-display text-sm font-semibold text-brand-700">Estimate your usage</div>
          <div className="mb-3 text-xs text-gray-600">
            Connect as many client organisations as you need — there is no plan to change. Move the slider to
            see what usage beyond the first {ADVICEPREP_ENTRY_CONNECTIONS} would add
            {isTrialing ? ' once the trial ends' : ''}.
          </div>
          <ConnectionsEstimator connections={connections} onChange={setConnections} accent="#15b79e" />
        </div>
      )}

      {/* Wording is provisional pending sign-off (see adviceprep_task.md "Open"). */}
      <Modal
        open={confirmSubscribeNow}
        title="End your free trial and subscribe now?"
        okText="Subscribe now"
        cancelText="Keep my trial"
        okButtonProps={{ loading: subscribingNow }}
        onOk={() => void handleConfirmSubscribeNow()}
        onCancel={() => setConfirmSubscribeNow(false)}
      >
        <div className="flex gap-2 text-sm text-gray-700">
          <ExclamationCircleOutlined className="mt-0.5 text-amber-500" />
          <div className="space-y-2">
            <p className="m-0">
              Subscribing now ends your free trial immediately.{' '}
              {trialDaysLeft
                ? `Your remaining ${trialDaysLeft} trial ${trialDaysLeft === 1 ? 'day' : 'days'} will be lost.`
                : 'Any remaining trial days will be lost.'}
            </p>
            <p className="m-0">
              You will be charged {formatAdvicePrepCurrency(flatFeeAfterTrial, 2)} today, and each month from today.
              Your {ADVICEPREP_TRIAL_CONNECTIONS} free trial connections become part of your paid plan.
            </p>
          </div>
        </div>
      </Modal>
    </Card>
  )
}
